import {
  Caption1,
  Checkbox,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Delete16Regular } from "@fluentui/react-icons";
import { useMemo, useRef, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogField, DialogGrid2 } from "@/components/app-dialog";
import { FuiButton, FuiDropdown, FuiInput } from "@/components/fui-control";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import type { EmploymentType, OrganizationTeam, SkillGrade, UserResource } from "@/engine/types";
import { EMPLOYMENT_OPTIONS, GRADE_OPTIONS } from "./resource-meta";
import { DEFAULT_TEAM_COLOR, DEFAULT_TEAM_ICON, TeamAppearanceFields, teamIconById } from "./team-appearance";

const useStyles = makeStyles({
  /**
   * Starts at the 24px dialog input height and grows when chips fill the row.
   * The caret stays in the field: leftover space on the same line, or the next line.
   */
  skillControl: {
    width: "100%",
    rowGap: "4px",
    columnGap: "4px",
    "&&": {
      minWidth: 0,
      minHeight: "24px",
      height: "auto",
      alignItems: "center",
      flexWrap: "wrap",
      paddingTop: "2px",
      paddingBottom: "2px",
      paddingLeft: tokens.spacingHorizontalS,
      paddingRight: tokens.spacingHorizontalS,
    },
  },
  skillInput: {
    "&&": {
      flex: "1 0 72px",
      minWidth: "72px",
      fontSize: tokens.fontSizeBase200,
      lineHeight: tokens.lineHeightBase200,
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  /** Flatten the group so chips and the input share one wrapping row. */
  skillGroup: {
    "&&": {
      display: "contents",
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  /** extra-small chip (20px). Label stays caption1 so it matches dialog input text (12px / 16px). */
  skillTag: {
    maxWidth: "220px",
    "&& .fui-InteractionTagPrimary": {
      minWidth: 0,
      overflow: "hidden",
    },
    "&& .fui-InteractionTagPrimary__primaryText": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: tokens.fontSizeBase200,
      lineHeight: tokens.lineHeightBase200,
      paddingBottom: 0,
    },
  },
  count: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  pick: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "220px",
    overflow: "auto",
    minWidth: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "4px",
  },
  rowCheck: {
    "&&": {
      width: "100%",
      maxWidth: "100%",
    },
    minHeight: "32px",
    alignItems: "center",
    paddingLeft: "8px",
    paddingRight: "8px",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  rowIndicator: {
    "&&": {
      alignSelf: "center",
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
  },
  personLabel: {
    "&&": {
      alignSelf: "center",
      marginTop: 0,
      marginBottom: 0,
    },
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    minWidth: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "8px",
    paddingRight: 0,
    fontWeight: tokens.fontWeightRegular,
  },
  memberName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  teamName: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
  },
  quiet: { color: tokens.colorNeutralForeground3 },
});

export type TeamPersonChoice = {
  id: string;
  name: string;
  teamName?: string;
};

/* ---------------------------------------------------------------- team */

export function TeamFormDialog({
  open,
  initial,
  onClose,
  onSave,
  onDelete,
  people,
  memberIds,
}: {
  open: boolean;
  initial?: OrganizationTeam | null;
  onClose: () => void;
  onSave: (team: Omit<OrganizationTeam, "id">, memberIds?: string[]) => void;
  /** 편집 안에서만. 확인 뒤에 팀을 지운다. */
  onDelete?: () => void;
  /** 넣거나 뺄 수 있는 사람. 없으면 소속 칸을 두지 않는다. */
  people?: TeamPersonChoice[];
  /** 이 팀에 이미 있는 사람. */
  memberIds?: string[];
}) {
  const styles = useStyles();
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [color, setColor] = useState(initial?.color ?? DEFAULT_TEAM_COLOR);
  const [icon, setIcon] = useState(teamIconById(initial?.icon)?.id ?? DEFAULT_TEAM_ICON);
  const [chosen, setChosen] = useState<string[]>(memberIds ?? []);
  const [askDelete, setAskDelete] = useState(false);
  const valid = name.trim().length > 0;
  const toggleMember = (id: string, on: boolean) => {
    setChosen((ids) => (on ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((item) => item !== id)));
  };
  const submit = () => {
    if (!valid) return;
    onSave(
      { name: name.trim(), description: description.trim() || undefined, color, icon, leaderId: initial?.leaderId },
      people ? chosen : undefined,
    );
  };
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="md"
      title={initial ? "팀 정보 수정" : "새 팀 추가"}
      footerStart={
        initial && onDelete ? (
          <FuiButton appearance="subtle" density="dialog" tone="danger" icon={<Delete16Regular />} onClick={() => setAskDelete(true)}>
            팀 삭제
          </FuiButton>
        ) : undefined
      }
      footer={<DialogActionsBar onCancel={onClose} onConfirm={submit} confirmLabel={initial ? "저장" : "생성"} disabled={!valid} />}
    >
      <DialogField label="팀 이름" required>
        <FuiInput density="dialog" value={name} autoFocus onChange={(_, d) => setName(d.value)} placeholder="예: 플랫폼 엔지니어링팀" onKeyDown={(e) => e.key === "Enter" && submit()} />
      </DialogField>
      <DialogField label="설명">
        <FuiInput density="dialog" value={description} onChange={(_, d) => setDescription(d.value)} placeholder="예: 공통 프레임워크 및 클라우드 인프라" />
      </DialogField>
      <TeamAppearanceFields icon={icon} color={color} onIconChange={setIcon} onColorChange={setColor} />
      {people ? (
        <DialogField
          label={<>멤버<span className={styles.count}> · {chosen.length}명</span></>}
          hint={people.length > 0 ? "한 사람의 팀은 하나입니다. 고르면 소속이 이 팀으로 바뀝니다." : undefined}
        >
          {people.length === 0 ? (
            <Caption1 className={styles.quiet}>넣을 사람이 없습니다</Caption1>
          ) : (
            <div className={styles.pick} role="group" aria-label="멤버">
              {people.map((person) => (
                <Checkbox
                  key={person.id}
                  className={styles.rowCheck}
                  indicator={{ className: styles.rowIndicator }}
                  label={{
                    className: styles.personLabel,
                    children: (
                      <>
                        <PersonAvatar name={person.name} size={20} tip={false} />
                        <span className={styles.memberName} title={person.name}>{person.name}</span>
                        {person.teamName ? <Caption1 className={styles.teamName}>{person.teamName}</Caption1> : null}
                      </>
                    ),
                  }}
                  checked={chosen.includes(person.id)}
                  onChange={(_, data) => toggleMember(person.id, Boolean(data.checked))}
                />
              ))}
            </div>
          )}
        </DialogField>
      ) : null}
      <ConfirmDialog
        open={askDelete}
        title="팀 삭제"
        message="팀을 삭제할까요? 소속된 사람의 팀은 비워지고, 인력 기록은 남습니다."
        confirmLabel="삭제"
        onCancel={() => setAskDelete(false)}
        onConfirm={() => {
          setAskDelete(false);
          onDelete?.();
          onClose();
        }}
      />
    </AppDialog>
  );
}

/* ---------------------------------------------------------------- resource */

const WORKING_DAYS = 21;

function appendSkills(base: string[], raw: string): string[] {
  const next = [...base];
  const seen = new Set(base.map((skill) => skill.toLowerCase()));
  for (const part of raw.split(",")) {
    const skill = part.trim();
    if (!skill) continue;
    const key = skill.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(skill);
  }
  return next;
}

/** Freeform skills. Chips are Fluent Tag extra-small so the label matches dialog input text. */
function SkillTagsField({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const styles = useStyles();
  const [query, setQuery] = useState("");
  const valueRef = useRef(value);
  const queryRef = useRef(query);
  valueRef.current = value;
  queryRef.current = query;

  const commit = (raw = queryRef.current) => {
    const next = appendSkills(valueRef.current, raw);
    valueRef.current = next;
    queryRef.current = "";
    onChange(next);
    setQuery("");
    return next;
  };

  return (
    <TagPicker
      noPopover
      open={false}
      selectedOptions={value}
      onOptionSelect={(_, data) => {
        valueRef.current = data.selectedOptions;
        onChange(data.selectedOptions);
      }}
    >
      <TagPickerControl className={styles.skillControl}>
        <TagPickerGroup className={styles.skillGroup} aria-label="보유 기술">
          {value.map((skill) => (
            <InteractionTag key={skill} value={skill} appearance="outline" shape="rounded" size="extra-small" className={styles.skillTag} title={skill}>
              <InteractionTagPrimary hasSecondaryAction>{skill}</InteractionTagPrimary>
              <InteractionTagSecondary aria-label={`${skill} 제거`} />
            </InteractionTag>
          ))}
        </TagPickerGroup>
        <TagPickerInput
          className={styles.skillInput}
          aria-label="보유 기술 추가"
          placeholder={value.length ? "" : "React, Spring, Kubernetes"}
          value={query}
          onChange={(e) => {
            const next = e.target.value;
            if (!next.includes(",")) {
              queryRef.current = next;
              setQuery(next);
              return;
            }
            const parts = next.split(",");
            const rest = (parts.pop() ?? "").replace(/^\s+/, "");
            const skills = appendSkills(valueRef.current, parts.join(","));
            valueRef.current = skills;
            queryRef.current = rest;
            onChange(skills);
            setQuery(rest);
            e.target.value = rest;
          }}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key === "Enter") {
              e.preventDefault();
              if (queryRef.current.trim()) commit();
            }
          }}
          onBlur={() => {
            if (queryRef.current.trim()) commit();
          }}
        />
      </TagPickerControl>
    </TagPicker>
  );
}

export function ResourceFormDialog({
  open,
  initial,
  teams,
  onClose,
  onSave,
}: {
  open: boolean;
  initial?: UserResource | null;
  teams: OrganizationTeam[];
  onClose: () => void;
  onSave: (res: Omit<UserResource, "id">) => void;
}) {
  const [name, setName] = useState(initial?.displayName ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [roleTitle, setRoleTitle] = useState(initial?.roleTitle ?? "");
  const [teamId, setTeamId] = useState(initial?.teamId ?? "");
  const [employmentType, setEmploymentType] = useState<EmploymentType>(initial?.employmentType ?? "permanent");
  const [grade, setGrade] = useState<SkillGrade>(initial?.grade ?? "senior");
  const [monthlyRate, setMonthlyRate] = useState(initial?.defaultMonthlyRate !== undefined ? String(initial.defaultMonthlyRate) : "");
  const [skills, setSkills] = useState(initial?.skills ?? []);
  const skillsRef = useRef(skills);
  const [notes, setNotes] = useState(initial?.notes ?? "");

  const setSkillsNow = (next: string[]) => {
    skillsRef.current = next;
    setSkills(next);
  };

  const emailOk = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const rateNum = monthlyRate.trim() ? Number(monthlyRate) : undefined;
  const rateOk = rateNum === undefined || (Number.isFinite(rateNum) && rateNum >= 0);
  const valid = name.trim().length > 0 && roleTitle.trim().length > 0 && emailOk && rateOk;

  const teamOptions = useMemo(() => [{ value: "", label: "(소속 없음)" }, ...teams.map((t) => ({ value: t.id, label: t.name }))], [teams]);

  const missing = useMemo(() => {
    const m: string[] = [];
    if (!name.trim()) m.push("이름");
    if (!roleTitle.trim()) m.push("직책");
    if (!emailOk) m.push("이메일 형식");
    if (!rateOk) m.push("단가");
    return m;
  }, [name, roleTitle, emailOk, rateOk]);

  const submit = () => {
    if (!valid) return;
    onSave({
      displayName: name.trim(),
      email: email.trim(),
      roleTitle: roleTitle.trim(),
      teamId: teamId || undefined,
      employmentType,
      grade,
      skills: skillsRef.current,
      defaultMonthlyRate: rateNum,
      defaultDailyRate: rateNum !== undefined ? Math.round(rateNum / WORKING_DAYS) : undefined,
      avatarColor: initial?.avatarColor,
      isActive: initial?.isActive ?? true,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="lg"
      title={initial ? "프로필 수정" : "새 인력 등록"}
      footerStart={missing.length > 0 && <Caption1>{missing.join(", ")} 입력이 필요합니다</Caption1>}
      footer={<DialogActionsBar onCancel={onClose} onConfirm={submit} disabled={!valid} />}
    >
      <DialogGrid2>
        <DialogField label="이름" required>
          <FuiInput density="dialog" value={name} autoFocus onChange={(_, d) => setName(d.value)} placeholder="예: 홍길동" />
        </DialogField>
        <DialogField label="이메일" hint={!emailOk ? "올바른 이메일 형식이 아닙니다" : undefined}>
          <FuiInput density="dialog" type="email" value={email} onChange={(_, d) => setEmail(d.value)} placeholder="user@company.com" />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogField label="직책 / 전문분야" required>
          <FuiInput density="dialog" value={roleTitle} onChange={(_, d) => setRoleTitle(d.value)} placeholder="예: 시니어 백엔드 PL" />
        </DialogField>
        <DialogField label="소속 팀">
          <FuiDropdown density="dialog" value={teamId} options={teamOptions} onValueChange={setTeamId} />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogField label="고용 형태">
          <FuiDropdown density="dialog" value={employmentType} options={EMPLOYMENT_OPTIONS} onValueChange={(v) => setEmploymentType(v as EmploymentType)} />
        </DialogField>
        <DialogField label="기술 등급">
          <FuiDropdown density="dialog" value={grade} options={GRADE_OPTIONS} onValueChange={(v) => setGrade(v as SkillGrade)} />
        </DialogField>
      </DialogGrid2>
      <DialogField label="기준 월 단가 (원가)" hint={rateNum !== undefined && rateOk ? `일 단가 약 ₩${Math.round(rateNum / WORKING_DAYS).toLocaleString("ko-KR")} (${WORKING_DAYS}일 기준)` : "비워두면 배정 시 개별 단가를 입력합니다"}>
        <FuiInput density="dialog" type="number" step={100000} value={monthlyRate} onChange={(_, d) => setMonthlyRate(d.value)} contentBefore="₩" placeholder="10,000,000" />
      </DialogField>
      <DialogField label="보유 기술">
        <SkillTagsField value={skills} onChange={setSkillsNow} />
      </DialogField>
      <DialogField label="메모">
        <FuiInput density="dialog" value={notes} onChange={(_, d) => setNotes(d.value)} placeholder="경력, 계약 특이사항 등" />
      </DialogField>
    </AppDialog>
  );
}

/** Small inline button used by cards to open editors without leaving the page. */
export function InlineAction({ icon, label, onClick }: { icon: React.ReactElement; label: string; onClick: () => void }) {
  return <FuiButton appearance="subtle" density="dialog" icon={icon} aria-label={label} title={label} onClick={onClick} />;
}
